import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { drawReportHeader, stampPageNumbers } from './reports/pdfChrome';
import { downloadBlob } from './reports/downloadBlob';
import { buildReportFilename } from './reports/reportFilename';

// Verify jsPDF and autotable are properly loaded
if (typeof jsPDF === 'undefined') {
  console.error('jsPDF not loaded properly');
}

if (typeof jsPDF.prototype.autoTable === 'undefined') {
  console.error('jsPDF autoTable plugin not loaded properly');
}

/**
 * Generates a PDF dispatch report for an employee
 * @param {Object} params - PDF generation parameters
 */
export const generateDispatchPDF = (params) => {
  const {
    employeeName,
    selectedDate,
    dispatchTasks = []
  } = params;

  try {
    // Create PDF in landscape orientation
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    // Page dimensions for landscape A4
    const pageWidth = doc.internal.pageSize.getWidth(); // ~297mm
    const pageHeight = doc.internal.pageSize.getHeight(); // ~210mm
    const margin = 15;

    // Format the date
    const formattedDate = new Date(selectedDate).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Header
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');

    // Employee name on top left
    doc.text(employeeName, margin, 20);

    // Date on top right
    const dateWidth = doc.getTextWidth(formattedDate);
    doc.text(formattedDate, pageWidth - margin - dateWidth, 20);

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    const title = 'Daily Dispatch Schedule';
    const titleWidth = doc.getTextWidth(title);
    doc.text(title, (pageWidth - titleWidth) / 2, 35);

    // Sort tasks by start time first
    const sortedTasks = [...dispatchTasks].sort((a, b) => {
      const timeA = new Date(a.startTime);
      const timeB = new Date(b.startTime);
      return timeA - timeB;
    });

    // Prepare table data from sorted tasks
    const tableData = sortedTasks.map(task => {
      const startTime = new Date(task.startTime).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      const endTime = new Date(task.endTime).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });

      const taskLocation = `${task.task}${task.location ? ` - ${task.location}` : ''}`;
      const notes = task.notes || '';

      return [startTime, endTime, taskLocation, notes];
    });

    // Calculate optimal font size to fit everything on one page
    const availableHeight = pageHeight - 60; // Space for header and margins
    const estimatedRowHeight = 8; // Base row height
    const maxRowsWithoutScaling = Math.floor(availableHeight / estimatedRowHeight);

    let scaleFactor = 1;
    if (tableData.length > maxRowsWithoutScaling) {
      scaleFactor = maxRowsWithoutScaling / tableData.length;
    }

    // Apply scaling if needed
    const fontSize = Math.max(8, Math.floor(10 * scaleFactor));
    const rowHeight = Math.max(6, Math.floor(estimatedRowHeight * scaleFactor));

    // AutoTable configuration
    const tableConfig = {
      head: [['Start Time', 'End Time', 'Task - Location', 'Notes']],
      body: tableData,
      startY: 45,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: fontSize,
        cellPadding: 3,
        overflow: 'linebreak',
        halign: 'left',
        valign: 'top'
      },
      headStyles: {
        fillColor: [41, 128, 185], // Nice blue color
        textColor: 255,
        fontStyle: 'bold',
        fontSize: fontSize + 1
      },
      columnStyles: {
        0: { cellWidth: 25 }, // Start Time
        1: { cellWidth: 25 }, // End Time
        2: { cellWidth: 80 }, // Task - Location
        3: { cellWidth: 'auto' } // Notes (takes remaining space)
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245] // Light gray for alternate rows
      },
      rowPageBreak: 'avoid',
      tableWidth: 'auto',
      theme: 'grid'
    };

    // Calculate if we need to adjust row heights for notes
    const maxNotesLength = Math.max(...tableData.map(row => (row[3] || '').length));
    if (maxNotesLength > 50) {
      // Increase row height for longer notes
      tableConfig.styles.minCellHeight = Math.max(rowHeight, 12);
    }

    // Generate the table
    doc.autoTable(tableConfig);

    // Check if table fits on one page and adjust if needed
    const finalY = doc.lastAutoTable.finalY;
    if (finalY > pageHeight - 20) {
      console.warn('Table exceeds page height, regenerating with smaller font...');

      // Clear the page and regenerate with smaller settings
      doc.deletePage(1);
      doc.addPage();

      // Recalculate with more aggressive scaling
      const newScaleFactor = (pageHeight - 80) / (tableData.length * estimatedRowHeight);
      const newFontSize = Math.max(6, Math.floor(8 * newScaleFactor));

      // Redraw header
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(employeeName, margin, 15);
      doc.text(formattedDate, pageWidth - margin - doc.getTextWidth(formattedDate), 15);

      doc.setFontSize(14);
      const smallTitle = 'Daily Dispatch Schedule';
      const smallTitleWidth = doc.getTextWidth(smallTitle);
      doc.text(smallTitle, (pageWidth - smallTitleWidth) / 2, 25);

      // Regenerate table with smaller settings
      doc.autoTable({
        ...tableConfig,
        startY: 30,
        styles: {
          ...tableConfig.styles,
          fontSize: newFontSize,
          cellPadding: 2
        },
        headStyles: {
          ...tableConfig.headStyles,
          fontSize: newFontSize + 1
        }
      });
    }

    // Standardized cover page inserted at the front (the schedule layout itself
    // is too dense for an inline header, so it gets a dedicated cover).
    doc.insertPage(1);
    doc.setPage(1);
    drawReportHeader(doc, {
      title: "Daily Dispatch Schedule",
      dateRange: `${employeeName} — ${formattedDate}`,
      generatedBy: params.generatedBy || employeeName || "Unknown",
    });

    // Standardized page numbers on every page
    stampPageNumbers(doc);

    return doc;
  } catch (error) {
    console.error('Error generating dispatch PDF:', error);
    throw error;
  }
};

/**
 * Downloads the dispatch PDF
 * @param {Object} params - PDF parameters
 */
export const downloadDispatchPDF = (params) => {
  try {
    const doc = generateDispatchPDF(params);
    const fileName = buildReportFilename(`Dispatch_${params.employeeName}`, {
      ext: "pdf",
    });
    downloadBlob(doc.output("blob"), fileName);
    return { success: true, fileName };
  } catch (error) {
    console.error('Error downloading dispatch PDF:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Opens the dispatch PDF in a new tab for preview
 * @param {Object} params - PDF parameters
 */
export const previewDispatchPDF = (params) => {
  try {
    const doc = generateDispatchPDF(params);
    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);

    window.open(pdfUrl, '_blank');

    // Clean up the URL after a delay
    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 1000);

    return { success: true };
  } catch (error) {
    console.error('Error previewing dispatch PDF:', error);
    return { success: false, error: error.message };
  }
};